import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Observation } from 'src/app/domain/observations/types/observations.types';
import {
  getMeasurementValue,
  getPresentMeasurementTypes,
  type MeasurementTypeId,
} from 'src/app/domain/observations/types/observations.lookups';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Dense list-row for a {@link Observation}. Type + formatted value + date + optional skill.
 *
 * A Observation document may carry values for multiple types at once, so the
 * caller can supply an explicit `type` input to render a specific one; if
 * omitted, the row shows the first populated type on the document.
 *
 * @security FERPA — tied to a student.
 *
 * @feature shared
 */
@Component({
  selector: 'app-observation-list-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChipComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <button
      type="button"
      class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
      [disabled]="!clickable()"
      [class.cursor-default]="!clickable()"
      (click)="onClick()"
      [attr.aria-label]="'Open measurement ' + (effectiveType() ?? '')"
    >
      @if (effectiveType(); as type) {
        <app-chip color="purple" size="xs">{{ type }}</app-chip>
      }

      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-semibold text-body">{{ formattedValue() }}</p>
        <p class="truncate text-xs text-muted">
          {{ dateLabel() }}
          @if (skillName()) {
            · {{ skillName() }}
          }
        </p>
      </div>

      @if (clickable()) {
        <app-icon name="chevron-forward" size="xs" color="medium" />
      }
    </button>
  `,
})
export class ObservationListItemComponent {
  /** The measurement to render. */
  readonly measurement = input.required<Observation>();

  /** Which measurement type to display. Defaults to the first populated type. */
  readonly type = input<MeasurementTypeId | null>(null);

  /** Optional resolved skill name. */
  readonly skillName = input<string | undefined>(undefined);

  /** Whether the row is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the row is clicked. */
  readonly rowClick = output<Observation>();

  /** Type currently driving the displayed value. */
  readonly effectiveType = computed<MeasurementTypeId | null>(() => {
    const explicit = this.type();
    if (explicit) return explicit;
    return getPresentMeasurementTypes(this.measurement())[0] ?? null;
  });

  readonly formattedValue = computed(() => {
    const m = this.measurement();
    const type = this.effectiveType();
    if (!type) return '—';
    const v = getMeasurementValue(m, type);
    switch (type) {
      case 'ACCURACY':
      case 'ENGAGEMENT':
      case 'TIME_ON_TASK':
      case 'OCCURRENCE':
        return v != null ? `${Math.round(v * 100)}%` : '—';
      case 'FLUENCY':
        return v != null ? `${v} correct/min` : '—';
      case 'FREQUENCY':
        return v != null ? `${v}` : '—';
      case 'DURATION':
        return v != null ? `${v}s` : '—';
      case 'LATENCY':
        return v != null ? `${v}s` : '—';
      case 'RATE':
        return v != null ? `${v}/min` : '—';
      case 'MAGNITUDE':
        return v != null ? `${v}` : '—';
      default:
        return v != null ? `${v}` : '—';
    }
  });

  readonly dateLabel = computed(() => {
    const d = this.measurement().inputtedDate;
    if (!d) return 'No date';
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  });

  onClick(): void {
    if (this.clickable()) {
      this.rowClick.emit(this.measurement());
    }
  }
}
